<template>
  <div>
    <div>
      <div id="map"></div>
    </div>
    <div class="table-responsive">
      <table class="table table-striped">
        <thead>
          <tr>
            <th scope="col">{{ $t('map.tbl_head_title') }}</th>
            <th scope="col">{{ $t('map.tbl_head_merchant') }}</th>
            <th scope="col">{{ $t('map.tbl_head_type') }}</th>
            <th scope="col">{{ $t('map.tbl_head_phase') }}</th>
            <th scope="col">lat/lng</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(c,i) in campaigns" :key="c.label" :id="'hover-helper-'+i">
            <td scope="row"><a :href="campaignUrl(c)">{{c.title}}</a></td>
            <td>{{ c.merchant_name }}</td>
            <td>{{ parseType(c) }}</td>
            <td>{{ parseStatus(c) }}</td>
            <td>{{c.lat}} / {{c.lng}}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>

import {
  apiCampaignsList,
} from "@/api/campaigns.js";

import mapsInit from "@/api/maps.js";
import { mapGetters } from "vuex";

export default {
  name: 'CampaignListMap',
  computed: {
    ...mapGetters({
      axiosBusy: "axiosIsBusy",
    })
  },
  data() {
    return {
      googleApiKey: this.$parent.googleApiKey,
      lat: this.$parent.locLat,
      lng: this.$parent.locLng,
      distance: 10000, // search_radius in meters
      quality: this.$parent.locQuality,
      google: null,
      gmap: null,
      ggeocoder: null,
      ginfowindow: null,
      campaigns: [],
      markers: [],
      markerOption: {
        free: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
        percent: "https://maps.google.com/mapfiles/ms/icons/yellow-dot.png",
        absolute: "https://maps.google.com/mapfiles/ms/icons/pink-dot.png"
      }
    };
  },
  watch: {
    quality: async function() {
      await this.fetchData();
      this.gmap.setCenter({lat:this.lat, lng:this.lng});
      await this.setCampaignMarker();
    }
  },
  async mounted() {
    try {
      this.google = await mapsInit(this.googleApiKey);
    } catch (error) {
      throw new Error(error);
    }
    // quality can be in decreasing order : USER, NAVIGATOR, GEOIP, FALLBACK
    switch (this.quality) {
      case "USER":
      case "NAVIGATOR": {
        break;
      }
      case "GEOIP":
      case "FALLBACK":
      default: {
        let old_loc = {
          lat: this.lat,
          lng: this.lat,
          quality: this.quality
        };
        try {
          this.navGeoLoc();
        } catch (error) {
          this.lat = old_loc.lat;
          this.lng = old_loc.lng;
          this.quality = old_loc.quality;
        }
        break;
      }
    }
    await this.fetchData();
    await this.renderMaps();
    this.setCampaignMarker();
  },
  methods: {
    campaignUrl(c) {
      return 'campaigns/'+c.uuid+'/'+c.safe_title;
    },
    imageUrl(c) {
      return c.campaign_image ? c.campaign_image.replace('{FORMAT}', "campaign-image-standard-320-format") : "";
    },
    parseBenefit(c) {
      let benefit = c.coupon_benefit;
      let orig = parseInt(benefit.original_value);
      let type = c.type.split('/')[1];
      let rv = "";
      let disc;
      switch (type) {
        case "absolute":
          disc = parseInt(benefit.abs_discount);
          rv = this.$t('map.benefit_abs_disc') + ": " + (disc / 100) + this.$t('generic.currency');
          break;
        case "percent":
          disc = parseInt(benefit.percent_discount);
          rv = this.$t('map.benefit_rel_disc') + ": " + disc + "%";
          break;

        case "free":
          rv = this.$t('map.benefit_free') + ": " + (orig / 100) + this.$t('generic.currency');
          break;

        default:
          break;
      }
      return rv;
    },
    parseType(c) {
      let type = c.type.split('/')[1];
      return this.$t('map.c_' + type);
    },
    parseStatus(c) {
      let status = c.current_status;
      let rv;
      switch (status) {
        case 'active':
        case 'ended':
          rv = this.$t('map.phase_' + status);
          break;

        case 'approved':
        case 'created':
        case 'start_review':
        default:
          rv = this.$t('map.phase_pending');
          break;
      }
      return rv;
    },
    async fetchData() {
      const rv = await apiCampaignsList(this.$userClient, {
        lat: this.lat,
        lng: this.lng,
        distance: this.distance,
        main_type: "coupon",
        sort: "running"
        //phase: "running"
      });
      if (rv) {
        this.campaigns = rv.rows;
      }
    },
    async renderMaps() {
      this.gmap = new this.google.maps.Map(
        document.getElementById('map'),
        {
          maxZoom: 16,
          minZoom: 10,
          zoom : 12, // initial zoomLevel (less is zoom-out)
          center: { lat: this.lat, lng: this.lng }
        });

      this.infowindow = new this.google.maps.InfoWindow();

      // --- event-listeners for map --- //
      const that = this;
      this.gmap.addListener('idle', async function() {
        let currentCenter = that.gmap.getCenter();
        that.lat = currentCenter.lat();
        that.lng = currentCenter.lng();
        that.distance = 10000 * Math.pow(2,12-that.gmap.getZoom());
        await that.fetchData();
        await that.setCampaignMarker();
      });
    },
    setCampaignMarker() {
      this.deleteMarkers();
      this.campaigns.forEach((c,i) => {
        if (c.has_participated == "0") {
          let cType = c.type.split('/')[1];
          let mIcon;
          switch (cType) {
            case "free":
              mIcon = this.markerOption.free;
              break;
            case "absolute":
              mIcon = this.markerOption.absolute;
              break;
            case "percent":
              mIcon = this.markerOption.percent;
              break;
          }
          const marker = new this.google.maps.Marker({
            map: this.gmap,
            position: {lat:c.lat,lng:c.lng},
            title: c.title,
            icon : mIcon
          });
          const vm = this;
          marker.addListener('click', function() {
            vm.infowindow.setContent('<img src="' + vm.imageUrl(c) + '"/>' +
            '<h5>' + c.title + '</h5>' +
            '<div><p><ul>' +
            '<li>' + vm.$t('map.tbl_head_type') + ': ' + vm.parseType(c) + '</li>' +
            '<li>' + vm.$t('map.tbl_head_phase') + ': ' + vm.parseStatus(c) + '</li>' +
            '<li>' + vm.parseBenefit(c) + '</li>' +
            '<li>' + vm.$t('map.tbl_head_merchant') + ': ' + c.merchant_name + '</li>' +
            '</ul></p>'+
            '<a href="'+ vm.campaignUrl(c) +'">'+ vm.$t('map.info_party') +'</a></div>');
            vm.infowindow.open(vm.map, marker);
          });
          let elId = "hover-helper-"+i;
          this.google.maps.event.addDomListener(
            document.getElementById(elId),
            'mouseover',
            function() {
              marker.setAnimation(vm.google.maps.Animation.BOUNCE);
            }
          );
          this.google.maps.event.addDomListener(
            document.getElementById(elId),
            'mouseout',
            function() {
              marker.setAnimation(null);
            }
          );
          this.markers.push(marker);
        } else if (c.has_redeem) {
          // mark coupons if participated but not yet redeemed
        }
      });
    },
    setMapOnAll(map) {
      for (var i = 0; i < this.markers.length; i++) {
        this.markers[i].setMap(map);
      }
    },
    clearMarkers() {
      this.setMapOnAll(null);
    },
    deleteMarkers() {
      this.clearMarkers();
      this.markers = [];
    },
    // ---------- navigator-location and geo-ip ---------- //
    navGeoLoc() {
      navigator.geolocation.getCurrentPosition(
        this.callbackPosition,
        this.errCallbackPosition,
        {
          timeout: 5000,
          maximumAge: 0,
          enableHighAccuracy: true,
        }
      );
    },
    async callbackPosition (Position) {
      this.lat = Position.coords.latitude;
      this.lng = Position.coords.longitude;
      this.quality = "NAVIGATOR";
      await this.$frontendClient({
        url: "set-location",
        method: "post",
        params: {
          lat: this.lat,
          lng: this.lng,
          quality: this.quality,
        }
      });
    },
    errCallbackPosition (error) {
      alert("Could not get Location via native API\n"+error);
      //console.warn("Could not get Location via native API\n"+error);
      // ev. geoip?
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
#map {
  width: 60vw;
  height: 80vh;
}
@media (max-width: 1300px) {
    #map {
        margin: auto;
        width: 75vw;
    }
}
@media (max-width: 1000px) {
    #map {
        margin: auto;
        width: 85vw;
    }
}
@media (max-width: 790px) {
    #map {
        margin: auto;
        width: 90vw;
    }
}
@media (max-width: 720px) {
    #map {
        margin: auto;
        width: 95vw;
    }
}
</style>
